/**
 * VisibilityChip
 *
 * Sagt an einer FAQ-Karte, wer sie sehen kann - und ob sie dem Betrachter
 * gehoert oder mit ihm geteilt wurde.
 *
 * Vorher stand dort ein nacktes Schloss. Das beantwortet die Frage nur halb:
 * es sagt "privat", aber nicht, ob die FAQ dem Betrachter gehoert. Umgekehrt
 * waere es genauso falsch, den Besitz an die Stelle der Sichtbarkeit zu
 * setzen: "Meine" und "Organisation" liegen nicht auf derselben Ebene, und wer
 * nur "Meine" liest, weiss nicht mehr, ob die FAQ privat oder freigegeben ist.
 *
 * Deshalb traegt der Chip immer die Sichtbarkeit als Text, und das Eigentum
 * kommt als zusaetzliches Zeichen davor: ein Personensymbol fuer "gehoert
 * mir", ein Gruppensymbol fuer "mit mir geteilt". Der Tooltip schreibt beides
 * aus.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  Lock as LockIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { FAQDocumentResponse, DocumentVisibility } from '../../api/data-contracts';

export interface VisibilityChipProps {
  faq: FAQDocumentResponse;
  /** Die ID des angemeldeten Nutzers; ohne sie entfaellt der Eigentumsbezug. */
  currentUserId?: string | null;
  /** Der ausgeschriebene Sichtbarkeitstext fuer den Tooltip. */
  title?: string;
}

export const VisibilityChip = React.memo<VisibilityChipProps>(({
  faq,
  currentUserId,
  title,
}) => {
  const { t } = useTranslation();

  const isOwner = Boolean(currentUserId && faq.owner_id === currentUserId);
  const isSharedWithMe =
    !isOwner &&
    Boolean(currentUserId && faq.shared_with_users?.includes(currentUserId));

  const visibility = ((): { label: string; icon: React.ReactElement } => {
    switch (faq.visibility) {
      case DocumentVisibility.Public:
        return {
          label: t('faq.permissions.public'),
          icon: <PublicIcon fontSize="small" />,
        };
      case DocumentVisibility.Private:
        return {
          label: t('faq.permissions.private'),
          icon: <LockIcon fontSize="small" />,
        };
      default:
        return {
          label: t('faq.permissions.organization'),
          icon: <BusinessIcon fontSize="small" />,
        };
    }
  })();

  // Das Eigentum ersetzt die Sichtbarkeit nicht, es ergaenzt sie: das Symbol
  // wechselt, der Text bleibt.
  const icon = isOwner ? (
    <PersonIcon fontSize="small" />
  ) : isSharedWithMe ? (
    <GroupIcon fontSize="small" />
  ) : (
    visibility.icon
  );

  const ownership = isOwner
    ? t('faq.ownership.mine')
    : isSharedWithMe
      ? t('faq.ownership.sharedWithMe')
      : null;

  const tooltip = [ownership, title || visibility.label]
    .filter(Boolean)
    .join(' - ');

  return (
    <Tooltip title={tooltip}>
      <Chip
        icon={icon}
        label={visibility.label}
        size="small"
        variant="outlined"
        color={ownership ? 'primary' : 'default'}
        sx={{ height: 24 }}
      />
    </Tooltip>
  );
});

VisibilityChip.displayName = 'VisibilityChip';
